/**
 * Company Store - Zustand
 *
 * Centralized state management for companies
 *
 * Features:
 * - CRUD operations (Create, Read, Update, Delete)
 * - Real-time search and filtering
 * - Pagination support
 * - Loading and error states
 * - Optimistic UI updates
 * - Toast notifications
 * - Company status management (active, trial, suspended, cancelled)
 * - Package management
 *
 * Backend API: /api/companies
 *
 * @module useCompanyStore
 */

import { create } from 'zustand'
import { companyService } from '@services/companyService'
import toast from 'react-hot-toast'

/**
 * Company Store
 *
 * State Management for Companies
 */
export const useCompanyStore = create((set, get) => ({
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * List of all companies
   * @type {Array<Object>}
   */
  companies: [],

  /**
   * Currently selected company for viewing/editing
   * @type {Object|null}
   */
  selectedCompany: null,

  /**
   * Loading state for async operations
   * @type {boolean}
   */
  loading: false,

  /**
   * Error message if operation fails
   * @type {string|null}
   */
  error: null,

  /**
   * Filter and search parameters
   * @type {Object}
   */
  filters: {
    search: '',              // Search by company name
    package_id: 'all',       // Filter by package
    status: 'all',           // all, active, trial, suspended, cancelled
    sortBy: 'name',          // Field to sort by
    sortOrder: 'asc',        // asc or desc
  },

  /**
   * Pagination configuration
   * @type {Object}
   */
  pagination: {
    page: 1,                 // Current page
    limit: 25,               // Items per page
    total: 0,                // Total number of items
    totalPages: 0,           // Total number of pages
  },

  /**
   * Statistics about companies
   * @type {Object}
   */
  stats: {
    total: 0,
    active: 0,
    trial: 0,
    pending: 0,
    suspended: 0,
    cancelled: 0,
  },

  /**
   * Packages list for filter dropdown
   * @type {Array<Object>}
   */
  packages: [],

  // ============================================================================
  // ACTIONS - READ OPERATIONS
  // ============================================================================

  /**
   * Fetch all companies with current filters
   *
   * @returns {Promise<void>}
   */
  fetchCompanies: async () => {
    set({ loading: true, error: null })

    try {
      const { filters, pagination } = get()

      // Build query parameters
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      }

      if (filters.search) {
        params.search = filters.search
      }

      if (filters.package_id !== 'all') {
        params.package_id = filters.package_id
      }

      if (filters.status !== 'all') {
        params.status = filters.status
      }

      // Make API request via service
      const response = await companyService.getAll(params)

      // Backend returns { status, message, data: [...], meta: {...} }
      const companies = response.data || []
      const meta = response.meta || {}
      const total = meta.total || companies.length

      // Update state with response data
      set({
        companies: companies,
        pagination: {
          ...pagination,
          page: meta.page || pagination.page,
          total: total,
          totalPages: meta.total_pages || Math.ceil(total / pagination.limit),
        },
        loading: false,
      })

      // Update statistics
      get().calculateStats()

    } catch (error) {
      console.error('Failed to fetch companies:', error)
      set({
        error: error.message || 'Failed to load companies',
        loading: false,
      })
      toast.error('Failed to load companies')
    }
  },

  /**
   * Fetch single company by ID
   *
   * @param {number} id - Company ID
   * @returns {Promise<Object>} Company data
   */
  fetchCompanyById: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await companyService.getById(id)
      const company = response.data || response

      set({
        selectedCompany: company,
        loading: false,
      })

      return company

    } catch (error) {
      console.error('Failed to fetch company:', error)
      set({
        error: error.message || 'Company not found',
        loading: false,
      })
      toast.error('Company not found')
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - CREATE OPERATIONS
  // ============================================================================

  /**
   * Create new company
   *
   * @param {Object} data - Company data (includes send_activation flag)
   * @returns {Promise<Object>} Created company
   */
  createCompany: async (data) => {
    set({ loading: true, error: null })

    try {
      const response = await companyService.create(data)
      const newCompany = response.data || response

      // Extract package_name from nested package object if it exists
      if (newCompany.package && newCompany.package.title) {
        newCompany.package_name = newCompany.package.title
      }

      // Optimistic UI update - add to beginning of list
      set((state) => ({
        companies: [newCompany, ...state.companies],
        pagination: {
          ...state.pagination,
          total: state.pagination.total + 1,
        },
        loading: false,
      }))

      // Show appropriate success message based on action
      if (data.send_activation) {
        toast.success('Company created and activation email sent!')
      } else {
        toast.success('Company saved as draft (pending status)')
      }

      // Update statistics
      get().calculateStats()

      // Refresh the list to get the correct data with joins
      setTimeout(() => get().fetchCompanies(), 500)

      return newCompany

    } catch (error) {
      console.error('Failed to create company:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to create company'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - UPDATE OPERATIONS
  // ============================================================================

  /**
   * Update existing company
   *
   * @param {number} id - Company ID
   * @param {Object} data - Updated company data
   * @returns {Promise<Object>} Updated company
   */
  updateCompany: async (id, data) => {
    set({ loading: true, error: null })

    try {
      const response = await companyService.update(id, data)
      const updatedCompany = response.data || response

      // Optimistic UI update - replace in list
      set((state) => ({
        companies: state.companies.map((company) =>
          company.id == id ? updatedCompany : company
        ),
        selectedCompany: state.selectedCompany?.id == id
          ? updatedCompany
          : state.selectedCompany,
        loading: false,
      }))

      toast.success('Company updated successfully!')

      // Update statistics
      get().calculateStats()

      return updatedCompany

    } catch (error) {
      console.error('Failed to update company:', error)

      // Extract detailed error message
      let errorMessage = 'Failed to update company'

      if (error.response?.data) {
        const responseData = error.response.data

        if (responseData.messages) {
          if (typeof responseData.messages === 'object') {
            const errors = Object.values(responseData.messages).filter(msg => typeof msg === 'string')
            if (errors.length > 0) {
              errorMessage = errors.join(', ')
            }
          } else if (typeof responseData.messages === 'string') {
            errorMessage = responseData.messages
          }
        } else if (responseData.message) {
          errorMessage = responseData.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Activate company account
   *
   * @param {number} id - Company ID
   * @returns {Promise<Object>} Updated company
   */
  activateCompany: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await companyService.activate(id)
      const updatedCompany = response.data || response

      // Optimistic UI update
      set((state) => ({
        companies: state.companies.map((company) =>
          company.id == id ? updatedCompany : company
        ),
        selectedCompany: state.selectedCompany?.id == id
          ? updatedCompany
          : state.selectedCompany,
        loading: false,
      }))

      toast.success('Company activated successfully!')

      // Update statistics
      get().calculateStats()

      return updatedCompany

    } catch (error) {
      console.error('Failed to activate company:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to activate company'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Suspend company account
   *
   * @param {number} id - Company ID
   * @returns {Promise<Object>} Updated company
   */
  suspendCompany: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await companyService.suspend(id)
      const updatedCompany = response.data || response

      // Optimistic UI update
      set((state) => ({
        companies: state.companies.map((company) =>
          company.id == id ? updatedCompany : company
        ),
        selectedCompany: state.selectedCompany?.id == id
          ? updatedCompany
          : state.selectedCompany,
        loading: false,
      }))

      toast.success('Company suspended successfully!')

      // Update statistics
      get().calculateStats()

      return updatedCompany

    } catch (error) {
      console.error('Failed to suspend company:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to suspend company'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Cancel company subscription
   *
   * @param {number} id - Company ID
   * @returns {Promise<Object>} Updated company
   */
  cancelSubscription: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await companyService.cancel(id)
      const updatedCompany = response.data || response

      // Optimistic UI update
      set((state) => ({
        companies: state.companies.map((company) =>
          company.id == id ? updatedCompany : company
        ),
        selectedCompany: state.selectedCompany?.id == id
          ? updatedCompany
          : state.selectedCompany,
        loading: false,
      }))

      toast.success('Subscription cancelled successfully!')

      // Update statistics
      get().calculateStats()

      return updatedCompany

    } catch (error) {
      console.error('Failed to cancel subscription:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to cancel subscription'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - DELETE OPERATIONS
  // ============================================================================

  /**
   * Delete (archive) company
   * Archive - preserves data but marks as cancelled and soft deleted
   *
   * @param {number} id - Company ID
   * @returns {Promise<void>}
   */
  deleteCompany: async (id) => {
    set({ loading: true, error: null })

    try {
      await companyService.delete(id)

      // Optimistic UI update - mark as deleted
      set((state) => ({
        companies: state.companies.map((company) =>
          company.id == id ? { ...company, deleted_at: new Date().toISOString(), status: 'cancelled' } : company
        ),
        selectedCompany: state.selectedCompany?.id == id ? null : state.selectedCompany,
        loading: false,
      }))

      toast.success('Company archived successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters (deleted companies may be hidden)
      setTimeout(() => get().fetchCompanies(), 100)

    } catch (error) {
      console.error('Failed to delete company:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete company'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Restore archived company
   *
   * @param {number} id - Company ID
   * @returns {Promise<void>}
   */
  restoreCompany: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await companyService.restore(id)
      const restored = response.data || response

      // Optimistic UI update - use actual restored data from backend
      set((state) => ({
        companies: state.companies.map((company) =>
          company.id == id ? restored : company
        ),
        selectedCompany: state.selectedCompany?.id == id ? null : state.selectedCompany,
        loading: false,
      }))

      toast.success('Company restored successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters
      setTimeout(() => get().fetchCompanies(), 100)

    } catch (error) {
      console.error('Failed to restore company:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to restore company'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - FILTER & SEARCH
  // ============================================================================

  /**
   * Update search/filter parameters and refresh data
   *
   * @param {Object} newFilters - New filter values
   */
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 }, // Reset to page 1 on filter change
    }))

    // Fetch with new filters
    get().fetchCompanies()
  },

  /**
   * Update search term
   *
   * @param {string} searchTerm - Search query
   */
  setSearch: (searchTerm) => {
    get().setFilters({ search: searchTerm })
  },

  /**
   * Update package filter
   *
   * @param {string|number} packageId - Package ID (or 'all')
   */
  setPackageFilter: (packageId) => {
    get().setFilters({ package_id: packageId })
  },

  /**
   * Update status filter
   *
   * @param {string} status - Status filter (all, active, trial, suspended, cancelled)
   */
  setStatusFilter: (status) => {
    get().setFilters({ status })
  },

  /**
   * Update sorting
   *
   * @param {string} sortBy - Field to sort by
   * @param {string} sortOrder - Sort order (asc/desc)
   */
  setSorting: (sortBy, sortOrder = 'asc') => {
    get().setFilters({ sortBy, sortOrder })
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    set({
      filters: {
        search: '',
        package_id: 'all',
        status: 'all',
        sortBy: 'name',
        sortOrder: 'asc',
      },
      pagination: {
        ...get().pagination,
        page: 1,
      },
    })

    get().fetchCompanies()
  },

  // ============================================================================
  // ACTIONS - PAGINATION
  // ============================================================================

  /**
   * Go to specific page
   *
   * @param {number} page - Page number
   */
  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }))

    get().fetchCompanies()
  },

  /**
   * Change items per page
   *
   * @param {number} limit - Items per page
   */
  setPageLimit: (limit) => {
    set((state) => ({
      pagination: { ...state.pagination, limit, page: 1 },
    }))

    get().fetchCompanies()
  },

  /**
   * Go to next page
   */
  nextPage: () => {
    const { pagination } = get()
    if (pagination.page < pagination.totalPages) {
      get().setPage(pagination.page + 1)
    }
  },

  /**
   * Go to previous page
   */
  prevPage: () => {
    const { pagination } = get()
    if (pagination.page > 1) {
      get().setPage(pagination.page - 1)
    }
  },

  // ============================================================================
  // ACTIONS - UTILITIES
  // ============================================================================

  /**
   * Calculate statistics from current companies
   */
  calculateStats: () => {
    const { companies } = get()

    const stats = {
      total: companies.length,
      active: companies.filter((company) => company.status === 'active').length,
      trial: companies.filter((company) => company.status === 'trial').length,
      pending: companies.filter((company) => company.status === 'pending').length,
      suspended: companies.filter((company) => company.status === 'suspended').length,
      cancelled: companies.filter((company) => company.status === 'cancelled').length,
    }

    set({ stats })
  },

  /**
   * Select company for viewing/editing
   *
   * @param {Object|null} company - Company to select
   */
  selectCompany: (company) => {
    set({ selectedCompany: company })
  },

  /**
   * Clear selected company
   */
  clearSelection: () => {
    set({ selectedCompany: null })
  },

  /**
   * Fetch packages for filter dropdown
   *
   * @returns {Promise<void>}
   */
  fetchPackages: async () => {
    try {
      const response = await companyService.getPackages()
      const packages = response.data || []

      set({ packages })

    } catch (error) {
      console.error('Failed to fetch packages:', error)
      toast.error('Failed to load packages')
    }
  },

  /**
   * Reset entire store to initial state
   */
  reset: () => {
    set({
      companies: [],
      selectedCompany: null,
      loading: false,
      error: null,
      filters: {
        search: '',
        package_id: 'all',
        status: 'all',
        sortBy: 'name',
        sortOrder: 'asc',
      },
      pagination: {
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 0,
      },
      stats: {
        total: 0,
        active: 0,
        trial: 0,
        pending: 0,
        suspended: 0,
        cancelled: 0,
      },
      packages: [],
    })
  },
}))

export default useCompanyStore
